import { Injectable } from '@angular/core';
import { GtagConfig, GtagEvent, GtagPageview } from './gtag-interfaces';
import { NavigationEnd, Router } from '@angular/router';
import { filter, tap } from 'rxjs/operators';
import { GdprConsent } from '../../components/global/consent/consent.service';

declare var gtag: Gtag.Gtag;

@Injectable({
  providedIn: 'root'
})
export class GtagService {
  private readonly gtagConfig: GtagConfig = {
    trackingId: 'G-5KJZHLHZ78',
    trackPageviews: true,
  };

  private defaultConsentParams: Gtag.ConsentParams = {
    ad_storage: 'denied',
    ad_personalization: 'denied',
    ad_user_data: 'denied',
    analytics_storage: 'denied',
    personalization_storage: 'denied',
    functionality_storage: 'denied',
    security_storage: 'denied',
  };

  constructor(private router: Router) {
    if (this.gtagConfig.trackPageviews) {
      router.events
          .pipe(
              filter(event => event instanceof NavigationEnd),
              tap(event => {
                this.pageview();
              }),
          )
          .subscribe();
    }
  }

  event(action: string, params: GtagEvent = {}) {
    // try/catch to avoid cross-platform issues
    try {
      gtag('event', action, params);
      this.debug('event', this.gtagConfig.trackingId, action, params);
    } catch (err) {
      console.error('Google Analytics event error', err);
    }
  }

  pageview(params?: GtagPageview) {
    try {
      const defaults = {
        page_path: this.router.url,
        page_title: 'Angular App',
        page_location: window.location.href,
      };

      params = { ...defaults, ...params };
      this.config(params);
      this.debug('pageview', this.gtagConfig.trackingId, params);
    } catch (err) {
      console.error('Google Analytics pageview error', err);
    }
  }

  config(params: Gtag.ControlParams | Gtag.EventParams | Gtag.ConfigParams | Gtag.CustomParams = {}) {
    try {
      gtag('config', this.gtagConfig.trackingId, params);
    } catch (err) {
      console.error('Google Analytics config error', err);
    }
  }

  set(params: Gtag.CustomParams | boolean | string = {}) {
    try {
      gtag('set', this.gtagConfig.trackingId, params);
    } catch (err) {
      console.error('Google Analytics set error', err);
    }
  }

  /**
   * Sets default consent values for GA
   * @param consent
   */
  setDefaultConsent(consent: GdprConsent) {
    const consentParams = { ...this.defaultConsentParams };
    consentParams.analytics_storage = consent?.consents?.analytics ? 'granted' : 'denied';

    try {
      gtag('consent', 'default', consentParams);
    } catch (err) {
      console.error('Google Analytics consent error', err);
    }
  }

  /**
   * Update consent values for GA
   * @param consent
   */
  updateConsent(consent: GdprConsent) {
    const consentParams = { ...this.defaultConsentParams };
    consentParams.analytics_storage = consent?.consents?.analytics ? 'granted' : 'denied';

    try {
      gtag('consent', 'update', consentParams);
    } catch (err) {
      console.error('Google Analytics consent error', err);
    }
  }

  /**
   * Google example mentions so set this
   * https://developers.google.com/tag-platform/security/guides/consent
   */
  js() {
    try {
      gtag('js', new Date());
    } catch (err) {
      console.error('Google Analytics js error', err);
    }
  }

  private debug(...msg: any) {
    if (this.gtagConfig.debug) {
      console.log('angular-gtag:', ...msg);
    }
  }
}

